import { Box, Stack, Typography, useTheme } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { FormProvider, RHFTextField, LocationSelector } from "@app/components";
import { Strings, imageSource } from "@app/constants";
import { useRequestState, useResponsive } from "@app/hooks";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { contactUs } from "../slice";
import { selectContactUs } from "../selectors";
import { yupResolver } from "@hookform/resolvers/yup";
import { contactUsSchema } from "../validators";

interface ContactUsFormProps {
  methods: any;
  loading: boolean;
  onSubmit: (form: any) => void;
}

const ContactUsForm = () => {
  const theme = useTheme();
  const isMobile = useResponsive("down", "md");
  const dispatch = useDispatch();

  const methods = useForm({
    resolver: yupResolver(contactUsSchema),
    defaultValues: {
      name: "",
      email: "",
      state: "",
      city: "",
      zipCode: "",
      message: "",
    },
  });
  const { reset } = methods;
  const onSubmit = (form: any) => {
    dispatch(
      contactUs({
        name: form.name,
        email: form.email,
        message: form.message,
        state: form.state,
        city: form.city,
        zipCode: form.zipCode,
      }),
    );
    reset();
  };

  const { loading } = useRequestState({
    stateSelector: selectContactUs,
  });

  return (
    <>
      <Stack sx={{ width: "100%" }}>
        <FormProvider methods={methods}>
          <Typography
            textAlign="center"
            fontSize={28}
            fontWeight={700}
            fontFamily="Work Sans"
            sx={{
              mb: 38,
              textDecoration: "underline",
              textDecorationColor: theme.palette.primary.main,
              textUnderlineOffset: 5,
            }}
          >
            {Strings.common.contactUs}
          </Typography>

          <Stack
            alignItems="center"
            sx={{
              backgroundColor: theme.palette.primary.main,
              height: "auto",
            }}
          >
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: 1,
                boxShadow: 3,
                p: { xs: 3, sm: 4 },
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                gap: 4,
                width: { xs: "90%", md: "75%", lg: "65%" },
                mt: -35,
              }}
            >
              <Stack spacing={2} sx={{ width: { xs: "100%", md: "50%" } }}>
                <RHFTextField
                  name="name"
                  label="Your Name"
                  fullWidth
                  required
                />
                <RHFTextField
                  name="email"
                  label="Your Email"
                  fullWidth
                  required
                />
                <LocationSelector variant="default" />
                <RHFTextField
                  name="message"
                  label="Your Message"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  sx={{
                    backgroundColor: theme.palette.grey[50],
                    fontFamily: "Work Sans",
                    fontWeight: "400",
                    color: theme.palette.grey[600],
                    fontSize: 14,
                  }}
                />
                <LoadingButton
                  loading={loading}
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    textTransform: "uppercase",
                    fontFamily: "Work Sans",
                    width: "55%",
                    p: 1.5,
                  }}
                  onClick={methods.handleSubmit(onSubmit)}
                >
                  {Strings.footer.sendAMessage}
                </LoadingButton>
              </Stack>
              {!isMobile && (
                <Box
                  component="img"
                  src={imageSource.key}
                  alt="contact"
                  sx={{
                    width: { xs: "100%", md: "50%" },
                    alignSelf: "stretch",
                    minHeight: 325,
                    borderRadius: 1,
                    objectFit: "cover",
                  }}
                />
              )}
            </Box>
          </Stack>
        </FormProvider>
      </Stack>
    </>
  );
};

export { ContactUsForm };
